import { useEffect } from "react";
import { requestIntercomMessengerSession } from "../api/intercomMessenger";
import { getStorage } from "../auth/utils";
import { getIntercomAppId, loadIntercomMessenger, INTERCOM_SDK_STATE_EVENT } from "../intercom/workspace";
import { waitForDomSettled } from "../intercom/domSettled";

const API_BASES = new Set([
  "https://api-iam.intercom.io",
  "https://api-iam.eu.intercom.io",
  "https://api-iam.au.intercom.io",
]);

// Tokens stay in memory. Never fall back to unsigned boot or persisted user data.
export default function useIntercomMessenger({ userId, clubId, impersonating, language }) {
  // Manager uses "gr" internally; Intercom expects the ISO language code "el".
  const messengerLanguage = language === "en" ? "en" : "el";
  useEffect(() => {
    let active = true;
    let booted = false;
    let pending = false;
    let timer;
    let controller;
    let expiresAt = 0;
    let failures = 0;
    let sdkFailures = 0;
    let requestGeneration = 0;
    const appId = getIntercomAppId();
    const firebaseUid = getStorage().getItem("firebaseUid");
    const shutdown = () => {
      // A third-party SDK failure must not escape React effects or interrupt
      // retry scheduling. Invalidate our session even if SDK cleanup fails.
      try {
        if (typeof window.Intercom === "function") window.Intercom("shutdown");
      } catch (_) { /* Messenger is optional; keep Manager running. */ }
      finally {
        booted = false;
        expiresAt = 0;
      }
    };
    const isCurrent = () => active && !impersonating && userId && clubId && firebaseUid &&
      !getStorage().getItem("impersonate") && getStorage().getItem("token") &&
      getStorage().getItem("firebaseUid") === firebaseUid &&
      getStorage().getItem("activeClubId") === String(clubId);
    const schedule = (delay) => {
      clearTimeout(timer);
      timer = setTimeout(refresh, delay);
    };
    async function refresh() {
      if (!active) return;
      if (!isCurrent() || !appId) { shutdown(); return; }
      if (pending) return;
      // A failed SDK download discards its queue; the replacement needs boot.
      if (typeof window.Intercom !== "function") { booted = false; expiresAt = 0; }
      if (!loadIntercomMessenger(appId)) {
        if (++failures <= 3) schedule(60000);
        return;
      }
      pending = true;
      const generation = ++requestGeneration;
      controller = new AbortController();
      try {
        const { data } = await requestIntercomMessengerSession(clubId, {
          expectedFirebaseUid: firebaseUid, signal: controller.signal, timeout: 10000,
        });
        if (!active || generation !== requestGeneration) return;
        if (!isCurrent()) { shutdown(); return; }
        if (data?.userId !== String(userId) || data?.clubId !== String(clubId) ||
            data?.appId !== appId || !API_BASES.has(data?.apiBase) ||
            typeof data?.jwt !== "string" || !data.jwt ||
            !Number.isSafeInteger(data?.sessionDurationMs) || data.sessionDurationMs < 120000 || data.sessionDurationMs > 540000 ||
            !Number.isSafeInteger(data?.expiresAt) || data.expiresAt * 1000 <= Date.now() + 10000) {
          throw new Error("INTERCOM_SESSION_INVALID");
        }
        if (booted && Date.now() >= expiresAt) shutdown();
        if (!booted) {
          // Intercom matches tooltips once, at boot; wait for page data to render.
          // Start a fresh window after this signed response, including reboots.
          // The request signal also cancels observation if the SDK fails.
          await waitForDomSettled({ signal: controller.signal });
          if (!active || generation !== requestGeneration) return;
          if (!isCurrent()) { shutdown(); return; }
        }
        // All identifying/company attributes are inside Core's signed JWT.
        window.Intercom(booted ? "update" : "boot", {
          app_id: data.appId, api_base: data.apiBase, intercom_user_jwt: data.jwt,
          session_duration: data.sessionDurationMs,
          language_override: messengerLanguage,
        });
        booted = true;
        expiresAt = Math.min(data.expiresAt * 1000, Date.now() + data.sessionDurationMs);
        failures = 0;
        schedule(Math.max(1000, expiresAt - Date.now() - 60000));
      } catch (_) {
        if (!active || generation !== requestGeneration) return;
        shutdown();
        if (!isCurrent()) return;
        // A Messenger outage must not log sensitive responses, log the user
        // out of Manager, or resurrect an insecure session.
        if (++failures <= 3) schedule(60000);
      } finally {
        if (generation === requestGeneration) pending = false;
      }
    }
    const onSdkState = (event) => {
      if (!active || event.detail?.appId !== appId) return;
      if (event.detail.status === 'ready') { sdkFailures = 0; return; }
      if (event.detail.status !== 'error') return;
      // Invalidate a response that could otherwise boot after its SDK failed.
      ++requestGeneration;
      controller?.abort();
      pending = false;
      shutdown();
      clearTimeout(timer);
      if (isCurrent() && ++sdkFailures <= 3) schedule(60000);
    };
    const onFocus = () => {
      if (!isCurrent()) { shutdown(); return; }
      if (Date.now() >= expiresAt - 60000) { failures = 0; sdkFailures = 0; void refresh(); }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") onFocus(); };
    const onStorage = () => { if (!isCurrent()) shutdown(); };

    // Clear a previous account/club session before requesting the new identity.
    shutdown();
    window.addEventListener(INTERCOM_SDK_STATE_EVENT, onSdkState);
    void refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      clearTimeout(timer);
      controller?.abort();
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(INTERCOM_SDK_STATE_EVENT, onSdkState);
      document.removeEventListener("visibilitychange", onVisibility);
      shutdown();
    };
    // Reboot with a fresh signed session on language changes so Messenger UI
    // and outbound targeting both receive the selected Manager language.
  }, [userId, clubId, impersonating, messengerLanguage]);
}
